import { useCallback, useEffect, useMemo, useState } from "react";

import useGetTaskTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTemplate";
import useGetTaskTypeTemplate from "@fartherfinance/frontend/api/TaskWorkflows/hooks/useGetTaskTypeTemplate";
import { TemplateInfo } from "@fartherfinance/frontend/api/TaskWorkflows/Types";
import { AdvisorRequestConfig } from "@fartherfinance/frontend/api/Types";

import { DEFAULT_TEMPLATE_ID } from "../../../constants";
import { extractTaskTypeId } from "../../utils/fieldRollover";
import { TemplateTransition } from "../types";

interface UseTemplateManagementProps {
  initialValues?: Record<string, unknown>;
  auth: AdvisorRequestConfig | null;
}

interface UseTemplateManagementReturn {
  currentTemplate: TemplateInfo | null;
  templateTransition: TemplateTransition;
  isLoadingTemplate: boolean;
  templateError: boolean;
  mainTemplateData: TemplateInfo | null;
  setCurrentTemplate: (template: TemplateInfo | null) => void;
  setTemplateTransition: (transition: TemplateTransition) => void;
}

export const useTemplateManagement = ({
  initialValues,
  auth,
}: UseTemplateManagementProps): UseTemplateManagementReturn => {
  // Determine template to load based on taskType from initial values
  const initialTaskTypeId = useMemo(() => {
    const taskTypeValue = initialValues?.taskType;
    return extractTaskTypeId(taskTypeValue);
  }, [initialValues?.taskType]);

  // Fetch templates (both hooks must be called to follow React hooks rules)
  const {
    data: taskTypeTemplateData,
    isLoading: isLoadingTaskTypeTemplate,
    hasError: taskTypeTemplateError,
  } = useGetTaskTypeTemplate(initialTaskTypeId, auth);

  const {
    data: defaultTemplateData,
    isLoading: isLoadingDefaultTemplate,
    hasError: defaultTemplateError,
  } = useGetTaskTemplate(DEFAULT_TEMPLATE_ID, auth);

  // Choose which template data to use and parse it
  const mainTemplateData = useMemo(() => {
    const rawData = initialTaskTypeId
      ? taskTypeTemplateData
      : defaultTemplateData;
    if (!rawData) {
      return null;
    }

    const parsedTemplate = TemplateInfo.safeParse(rawData);
    if (parsedTemplate.success) {
      return parsedTemplate.data;
    } else {
      console.error(
        "[useTemplateManagement] Failed to parse main template:",
        parsedTemplate.error
      );
      return null;
    }
  }, [initialTaskTypeId, taskTypeTemplateData, defaultTemplateData]);
  const isMainTemplateLoading = initialTaskTypeId
    ? isLoadingTaskTypeTemplate
    : isLoadingDefaultTemplate;
  const mainTemplateHasError = initialTaskTypeId
    ? taskTypeTemplateError
    : defaultTemplateError;

  // Template state
  const [currentTemplate, setCurrentTemplate] = useState<TemplateInfo | null>(
    mainTemplateData ?? null
  );
  const [templateTransition, setTemplateTransition] =
    useState<TemplateTransition>({
      oldTemplate: null,
      newTemplate: null,
      isTransitioning: false,
    });

  // Memoize setters to prevent unnecessary re-renders
  const memoizedSetCurrentTemplate = useCallback(
    (template: TemplateInfo | null) => {
      setCurrentTemplate(template);
    },
    []
  );

  const memoizedSetTemplateTransition = useCallback(
    (transition: TemplateTransition) => {
      setTemplateTransition(transition);
    },
    []
  );

  // Initialize currentTemplate when main template data is loaded
  useEffect(() => {
    if (mainTemplateData && !currentTemplate) {
      setCurrentTemplate(mainTemplateData);
    }
  }, [mainTemplateData, currentTemplate]);

  return useMemo(
    () => ({
      currentTemplate,
      templateTransition,
      isLoadingTemplate: isMainTemplateLoading,
      templateError: mainTemplateHasError,
      mainTemplateData,
      setCurrentTemplate: memoizedSetCurrentTemplate,
      setTemplateTransition: memoizedSetTemplateTransition,
    }),
    [
      currentTemplate,
      templateTransition,
      isMainTemplateLoading,
      mainTemplateHasError,
      mainTemplateData,
      memoizedSetCurrentTemplate,
      memoizedSetTemplateTransition,
    ]
  );
};
